import React, { useState, useEffect, useContext } from 'react';
// Remove direct Firebase imports
// import { db } from '../../firebase/firebase';
// import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc, Timestamp } from "firebase/firestore";
import { AuthContext } from '../../context/AuthContext';
import { Table, Button, Form, Alert, Modal, Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { 
  PeopleFill, 
  Plus, 
  CreditCard, 
  Pencil, 
  Trash, 
  CheckCircle, 
  Clock 
} from 'react-bootstrap-icons';
import './Admin.css';
import './UserManagement.css';
import UserEditModal from './UserEditModal';
// Add UserService import
import { services } from '../../Services/AdminService';

const UserManagement = ({ onUserCountChange }) => {
  const { user: adminUser } = useContext(AuthContext);
  // Get UserService instance
  const userService = services.users;
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState(null);

  // User approval states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedUserForApproval, setSelectedUserForApproval] = useState(null);
  const [approvalAction, setApprovalAction] = useState(''); // 'approve', 'reject', 'suspend'
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected', 'suspended'
  const [searchQuery, setSearchQuery] = useState('');

  // Enhanced User Management State
  const [showUserModal, setShowUserModal] = useState(false);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    role: 'basic',
    customer_id: '',
    subscriptions: []
  });
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    planId: '',
    planName: '',
    status: 'active',
    startDate: '',
    endDate: '',
    autoRenew: true
  });

  // User Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Fetch users from Firestore
  const fetchUsers = async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const result = await userService.getAllUsers();
      if (result.success) {
        const fetchedUsers = result.data;
        // Debug: Log user statuses to understand the data
        console.log('🔍 Fetched users:', fetchedUsers.map(u => ({ id: u.id, name: u.name, status: u.status, role: u.role })));
        setUsers(fetchedUsers);
      } else {
        setError(result.message || 'Failed to fetch users');
      }
    } catch (err) {
      console.error("Error fetching users: ", err);
      setError("Failed to load users. Please try again later.");
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch available plans
  const fetchAvailablePlans = async () => {
    try {
      console.log('🔍 Fetching available plans...');
      const fetchedPlans = await userService.getPricingPlans();
      console.log('📊 Fetched plans:', fetchedPlans);
      setAvailablePlans(fetchedPlans);
      
      if (fetchedPlans.length === 0) {
        console.warn('⚠️ No pricing plans found in database.');
        setError('No pricing plans found. Please run One-Time Data Setup to seed the database.');
      }
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      setError('Error fetching available plans: ' + error.message);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAvailablePlans();
  }, []);

  // User approval functions
  const handleShowApprovalModal = (user, action) => {
    setSelectedUserForApproval(user);
    setApprovalAction(action);
    setApprovalNotes('');
    setRejectionReason('');
    setShowApprovalModal(true);
  };

  const handleCloseApprovalModal = () => {
    setShowApprovalModal(false);
    setSelectedUserForApproval(null);
    setApprovalAction('');
    setApprovalNotes('');
    setRejectionReason('');
  };

  const handleUserApproval = async () => {
    if (!selectedUserForApproval) return;

    try {
      const result = await userService.updateUserStatus(
        selectedUserForApproval.id,
        approvalAction,
        approvalNotes,
        rejectionReason
      );

      if (result.success) {
        // Update local state
        const updatedUsers = users.map(user => 
          user.id === selectedUserForApproval.id 
            ? { ...user, ...result.data }
            : user
        );
        setUsers(updatedUsers);

        handleCloseApprovalModal();
        setError(null);
        // Notify parent component to refresh user count
        if (onUserCountChange) onUserCountChange();
      } else {
        setError(result.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Error updating user status: ' + error.message);
    }
  };

  // Get unique statuses from actual user data
  const getAvailableStatuses = () => {
    const statuses = new Set();
    users.forEach(user => {
      if (user.status) {
        statuses.add(user.status);
      } else {
        statuses.add('pending'); // Default status for users without status
      }
    });
    return Array.from(statuses).sort();
  };

  // Filter users by status and search query
  const filteredUsers = users.filter(user => {
    // Status filter
    if (statusFilter !== 'all') {
      const userStatus = user.status || 'pending'; // Default to 'pending' if status is undefined
      if (userStatus !== statusFilter) return false;
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const searchableFields = [
        user.name || '',
        user.email || '',
        user.customer_id || '',
        user.role || ''
      ];
      
      return searchableFields.some(field => 
        field.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    if (!status) return 'secondary'; // Handle undefined status
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'danger';
      case 'suspended': return 'secondary';
      default: return 'secondary';
    }
  };

  // Enhanced User Management Functions
  const handleShowUserModal = () => {
    setUserFormData({
      name: '',
      email: '',
      role: 'basic',
      customer_id: '',
      subscriptions: []
    });
    setShowUserModal(true);
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setUserFormData({
      name: '',
      email: '',
      role: 'basic',
      customer_id: '',
      subscriptions: []
    });
  };

  const handleUserFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateUser = async () => {
    try {
      const result = await userService.createUser(userFormData);
      if (result.success) {
        const newUser = result.data;
        setUsers([...users, { ...newUser, id: userFormData.email }]);
        handleCloseUserModal();
        setError(null);
        // Notify parent component to refresh user count
        if (onUserCountChange) onUserCountChange();
      } else {
        setError(result.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Error creating user: ' + error.message);
    }
  };

  const handleShowSubscriptionModal = (user) => {
    setSelectedUserId(user.id);
    setSubscriptionFormData({
      planId: '',
      planName: '',
      status: 'active',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      autoRenew: true
    });
    setShowSubscriptionModal(true);
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSelectedUserId(null);
    setSubscriptionFormData({
      planId: '',
      planName: '',
      status: 'active',
      startDate: '',
      endDate: '',
      autoRenew: true
    });
  };

  const handleSubscriptionFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSubscriptionFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddSubscription = async () => {
    try {
      const selectedPlan = availablePlans.find(plan => plan.id === subscriptionFormData.planId);
      if (!selectedPlan) {
        setError('Selected plan not found');
        return;
      }

      const user = users.find(u => u.id === selectedUserId);
      if (!user) {
        setError('User not found');
        return;
      }

      const result = await userService.addSubscription(
        selectedUserId,
        subscriptionFormData.planId,
        selectedPlan.name,
        subscriptionFormData.startDate,
        subscriptionFormData.endDate,
        subscriptionFormData.autoRenew
      );

      if (result.success) {
        const updatedUser = result.data;
        // Update local state
        const updatedUsers = users.map(u => 
          u.id === selectedUserId 
            ? { ...u, subscriptions: updatedUser.subscriptions, permissions: updatedUser.permissions }
            : u
        );
        setUsers(updatedUsers);

        handleCloseSubscriptionModal();
        setError(null);
        // Notify parent component to refresh user count
        if (onUserCountChange) onUserCountChange();
      } else {
        setError(result.message || 'Failed to add subscription');
      }
    } catch (error) {
      console.error('Error adding subscription:', error);
      setError('Error adding subscription: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (adminUser?.role !== 'admin') {
      setError("Unauthorized action.");
      return;
    }
    if (window.confirm('Are you sure you want to delete this user\'s Firestore record? This cannot be undone and does not delete their authentication account.')) {
      setError(null);
      try {
        const result = await userService.deleteUser(userId);
        if (result.success) {
          fetchUsers();
          // Notify parent component to refresh user count
          if (onUserCountChange) onUserCountChange();
        } else {
          setError(result.message || 'Failed to delete user');
        }
      } catch (err) {
        console.error("Error deleting user: ", err);
        setError("Failed to delete user.");
      }
    }
  };

  // User Edit Modal Handlers
  const handleShowEditModal = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
  };

  const handleUserUpdated = () => {
    // Refresh users list after update
    fetchUsers();
    // Notify parent component to refresh user count
    if (onUserCountChange) onUserCountChange();
  };

  return (
    <div className="admin-section">
      {/* Enhanced Filter and Search Section */}
      <div className="user-management-filters mb-4">
        <Card className="filter-card">
          <Card.Body className="p-3">
            <Row className="align-items-center g-3">
              <Col xs={12} md={3}>
                <div className="filter-group">
                  <label className="form-label small fw-semibold mb-2">Filter by Status</label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="form-select-sm"
                  >
                    <option value="all">All Users ({users.length})</option>
                    {getAvailableStatuses().map(status => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)} ({users.filter(u => (u.status || 'pending') === status).length})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="filter-group">
                  <label className="form-label small fw-semibold mb-2">Search Users</label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, email, customer ID, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-control-sm"
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="filter-group">
                  <label className="form-label small fw-semibold mb-2">&nbsp;</label>
                  <Button variant="primary" onClick={handleShowUserModal} className="add-user-btn w-100">
                    <Plus className="me-2" />
                    Add New User
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* Debug Info */}
      <div className="debug-info mb-3">
        <small className="text-muted">
          Total Users: {users.length} | Filter: {statusFilter} | Search: "{searchQuery}" | Filtered: {filteredUsers.length} | Available Statuses: {getAvailableStatuses().join(', ')}
        </small>
      </div>

      <Card>
        <Card.Body>
          {loadingUsers ? (
            <div className="text-center"><Spinner animation="border" /></div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Active Plans</th>
                    <th>Total Stocks</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg={user.role === 'admin' ? 'danger' : user.role === 'premium' ? 'primary' : 'secondary'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getStatusBadgeColor(user.status)}>
                          {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Pending'}
                        </Badge>
                      </td>
                      <td>
                        {user.subscriptions && user.subscriptions.length > 0 ? (
                          <div className="plan-badges">
                            {user.subscriptions
                              .filter(sub => sub.status === 'active')
                              .map((sub, index) => (
                                <Badge key={index} bg="info" className="me-1">
                                  {sub.planName}
                                </Badge>
                              ))}
                          </div>
                        ) : (
                          <span className="text-muted">No active plans</span>
                        )}
                      </td>
                      <td>
                        {user.permissions ? user.permissions.maxStocksPerQuarter : 0}
                      </td>
                      <td>
                        <div className="permissions-info">
                          <div>Reports: {user.permissions?.canViewReports ? '✓' : '✗'}</div>
                          <div>Charts: {user.permissions?.canViewCharts ? '✓' : '✗'}</div>
                          <div>Risk Levels: {user.permissions?.riskLevelAccess?.join(', ') || 'None'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleShowEditModal(user)}
                            title="Edit User"
                          >
                            <Pencil className="me-1" />
                            Edit
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => handleShowSubscriptionModal(user)}
                            title="Manage Subscriptions"
                          >
                            <CreditCard className="me-1" />
                            Subscriptions
                          </Button>
                          
                          {/* Approval Actions */}
                          {user.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => handleShowApprovalModal(user, 'approve')}
                                title="Approve User"
                              >
                                ✓ Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleShowApprovalModal(user, 'reject')}
                                title="Reject User"
                              >
                                ✗ Reject
                              </Button>
                            </>
                          )}
                          
                          {user.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline-warning"
                              onClick={() => handleShowApprovalModal(user, 'suspend')}
                              title="Suspend User"
                            >
                              ⏸️ Suspend
                            </Button>
                          )}
                          
                          {(user.status === 'rejected' || user.status === 'suspended') && (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => handleShowApprovalModal(user, 'approve')}
                              title="Reactivate User"
                            >
                              ✓ Reactivate
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete User"
                          >
                            <Trash className="me-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {filteredUsers.length === 0 && !loadingUsers && (
            <p className="text-muted text-center">No users found.</p>
          )}
        </Card.Body>
      </Card>

      {/* User Edit/Create Modal */}
      <Modal show={showUserModal} onHide={handleCloseUserModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={userFormData.name}
                onChange={handleUserFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={userFormData.email}
                onChange={handleUserFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={userFormData.role}
                onChange={handleUserFormChange}
                required
              >
                <option value="">Select Role</option>
                <option value="basic">Basic User</option>
                <option value="premium">Premium User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Customer ID</Form.Label>
              <Form.Control
                type="text"
                name="customer_id"
                value={userFormData.customer_id}
                onChange={handleUserFormChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseUserModal}>Cancel</Button>
          <Button variant="outline-primary" onClick={handleCreateUser}>
            Create User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Subscription Management Modal */}
      <Modal show={showSubscriptionModal} onHide={handleCloseSubscriptionModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage User Subscriptions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {availablePlans.length === 0 ? (
            <Alert variant="warning">
              <strong>No Pricing Plans Available</strong>
              <br />
              You need to seed the database with pricing plans first.
            </Alert>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Select Plan</Form.Label>
                <Form.Select
                  name="planId"
                  value={subscriptionFormData.planId}
                  onChange={handleSubscriptionFormChange}
                  required
                >
                  <option value="">Select a plan</option>
                  {availablePlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - ₹{plan.price} ({plan.maxStocksPerQuarter} stocks/quarter)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={subscriptionFormData.startDate}
                  onChange={handleSubscriptionFormChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={subscriptionFormData.endDate}
                  onChange={handleSubscriptionFormChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="autoRenew"
                  label="Auto-renew this subscription"
                  checked={subscriptionFormData.autoRenew}
                  onChange={handleSubscriptionFormChange}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseSubscriptionModal}>Cancel</Button>
          {availablePlans.length > 0 && (
            <Button variant="outline-primary" onClick={handleAddSubscription}>
              Add Subscription
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* User Approval Modal */}
      <Modal show={showApprovalModal} onHide={handleCloseApprovalModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedUserForApproval ? `Approve/Reject User: ${selectedUserForApproval.name}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <Form.Select
                name="approvalAction"
                value={approvalAction}
                onChange={(e) => setApprovalAction(e.target.value)}
                required
              >
                <option value="">Select Action</option>
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
                <option value="suspend">Suspend</option>
              </Form.Select>
            </Form.Group>
            {approvalAction === 'reject' || approvalAction === 'suspend' ? (
              <Form.Group className="mb-3">
                <Form.Label>Reason for Rejection/Suspension</Form.Label>
                <Form.Control
                  as="textarea"
                  name="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  placeholder="Provide a reason for the action"
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Notes (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  name="approvalNotes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any notes for the user"
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseApprovalModal}>Cancel</Button>
          <Button variant="outline-primary" onClick={handleUserApproval}>
            {selectedUserForApproval ? 'Confirm Action' : ''}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Edit Modal */}
      <UserEditModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        user={editingUser}
        onUserUpdated={handleUserUpdated}
        availablePlans={availablePlans}
      />
    </div>
  );
};

export default UserManagement; 